/**
 * 予約カレンダー用のAPI通信サービス
 */
import APIHelper from "../utils/apiUtils";
import { formatDate } from "../utils/dateUtils";
import type {
	SpaceData,
	ReservationsData,
	ReservationCheckResponse,
} from "../types";

// 日付をYYYY-MM-DD形式（API用）に変換
const formatAPIDate = (date: Date): string => {
	return formatDate(date, "yyyy-MM-dd");
};

// デバッグモード設定
const DEBUG = process.env.NODE_ENV === "development";

// モックデータの使用有無
const USE_MOCK = false; // trueにするとモックデータを使用する

/**
 * APIリクエストを記録するデバッグ関数
 * @param method - HTTPメソッド
 * @param url - リクエストURL
 * @param params - リクエストパラメータ
 */
const logApiRequest = (
	method: string,
	url: string,
	params: Record<string, unknown>,
) => {
  if (DEBUG) {
    console.group(`🌐 API送信: ${method} ${url}`);
    console.groupEnd();
  }
};

/**
 * APIレスポンスを記録するデバッグ関数
 * @param method - HTTPメソッド
 * @param url - リクエストURL
 * @param response - レスポンスデータ
 * @param duration - 処理時間（ミリ秒）
 */
const logApiResponse = (
	method: string,
	url: string,
	response: unknown,
	duration: number,
) => {
  if (DEBUG) {
    console.group(`✅ API受信: ${method} ${url} (${duration}ms)`);
    console.groupEnd();
  }
};

/**
 * APIエラーを記録するデバッグ関数
 * @param method - HTTPメソッド
 * @param url - リクエストURL
 * @param error - エラー情報
 */
const logApiError = (method: string, url: string, error: unknown) => {
  if (DEBUG) {
    console.group(`❌ APIエラー: ${method} ${url}`);
		console.error("エラー:", error);
    console.groupEnd();
  }
};

/**
 * URLSearchParamsに対応した形式にパラメータを変換
 * @param params - 変換するパラメータオブジェクト
 * @returns 文字列に変換されたパラメータオブジェクト
 */
const paramsToString = (
	params: Record<string, unknown>,
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(params)) {
    result[key] = String(value);
  }

  return result;
};

/**
 * スペース情報を取得する
 * @param id - スペースID
 * @returns スペース情報
 */
export const getSpaceData = async (id: number): Promise<SpaceData> => {
  const params = { id };
  const query = new URLSearchParams(paramsToString(params));
  const url = `/api/space.json?${query}`;

	logApiRequest("GET", url, params);
  const startTime = Date.now();

  try {
    // モックデータ（デバッグ用）
    if (DEBUG && USE_MOCK) {
      // 0.5秒後にモックデータを返す
			await new Promise((resolve) => setTimeout(resolve, 500));
      const mockData = {
        id,
        title: `テストスペース #${id}`,
        data: [
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
					{ start_at: "09:00", end_at: "22:00" },
        ],
        deadline: 60,
				reserve_days_ahead: 30,
      };

      const duration = Date.now() - startTime;
			logApiResponse("GET", url, mockData, duration);
      return mockData as SpaceData;
    }

    const response = await APIHelper.get(url);
    const duration = Date.now() - startTime;
		logApiResponse("GET", url, response, duration);
    return response;
  } catch (error) {
		logApiError("GET", url, error);
    throw error;
  }
};

/**
 * 予約情報を取得する
 * @param id - スペースID
 * @param date - 取得する週の開始日
 * @returns 予約情報
 */
export const getReservations = async (
	id: number,
	date: Date,
): Promise<ReservationsData> => {
	const formattedDate = formatAPIDate(date);
  const params = {
    id,
		date: formattedDate,
  };
  const query = new URLSearchParams(paramsToString(params));
  const url = `/api/reservations.json?${query}`;

	logApiRequest("GET", url, params);
  const startTime = Date.now();

  try {
    // モックデータ（デバッグ用）
    if (DEBUG && USE_MOCK) {
      // 0.5秒後にモックデータを返す
			await new Promise((resolve) => setTimeout(resolve, 500));

      // 日付文字列を生成
      const dateObj = new Date(date);
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(dateObj);
        d.setDate(d.getDate() + i);
				dates.push(formatDate(d, "yyyy-MM-dd"));
      }

      // ランダムな予約を生成
      const mockReservations = { reservations: {} } as ReservationsData;
      mockReservations.reservations = {};

      for (const d of dates) {
        // 50%の確率で予約あり
        if (Math.random() > 0.5) {
          mockReservations.reservations[d] = [
            { started_at: "10:00", duration: 120 },
						{ started_at: "14:00", duration: 90 },
          ];
        } else {
          mockReservations.reservations[d] = [];
        }
      }

      const duration = Date.now() - startTime;
			logApiResponse("GET", url, mockReservations, duration);
      return mockReservations;
    }

    const response = await APIHelper.get(url);
    const duration = Date.now() - startTime;
		logApiResponse("GET", url, response, duration);
    return response;
  } catch (error) {
		logApiError("GET", url, error);
    throw error;
  }
};

/**
 * 予約可能かどうかをチェックする
 * @param id - スペースID
 * @param startedAt - 開始日時（ISO8601形式）
 * @param endedAt - 終了日時（ISO8601形式）
 * @returns チェック結果
 */
export const checkReservationAvailability = async (
  id: number,
  startedAt: string,
	endedAt: string,
): Promise<ReservationCheckResponse> => {
  const params = {
    id,
    started_at: startedAt,
		ended_at: endedAt,
  };
  const query = new URLSearchParams(paramsToString(params));
  const url = `/api/is_reserve_able?${query}`;

	logApiRequest("GET", url, params);
  const startTime = Date.now();

  try {
    // モックデータ（デバッグ用）
    if (DEBUG && USE_MOCK) {
      // 0.5秒後にモックデータを返す
			await new Promise((resolve) => setTimeout(resolve, 500));

      const mockResponse = {
        result: "OK",
        message: null,
        space_id: id,
				name: `テストスペース #${id}`,
      };

      const duration = Date.now() - startTime;
			logApiResponse("GET", url, mockResponse, duration);
      return mockResponse;
    }

    const response = await APIHelper.get(url);
    const duration = Date.now() - startTime;
		logApiResponse("GET", url, response, duration);
    return response;
  } catch (error) {
		logApiError("GET", url, error);
    throw error;
  }
};

/**
 * 予約の価格を計算する
 * @param id - スペースID
 * @param startedAt - 開始日時（ISO8601形式）
 * @param endedAt - 終了日時（ISO8601形式）
 * @returns 予約価格
 */
export const calculatePrice = async (
  id: number,
  startedAt: string,
	endedAt: string,
): Promise<number> => {
  const params = {
    id,
    started_at: startedAt,
		ended_at: endedAt,
  };
  const query = new URLSearchParams(paramsToString(params));
  const url = `/api/calculate_price?${query}`;

	logApiRequest("GET", url, params);
  const startTime = Date.now();

  try {
    // モックデータ（デバッグ用）
    if (DEBUG && USE_MOCK) {
      // 0.5秒後にモックデータを返す
			await new Promise((resolve) => setTimeout(resolve, 500));

      // 所要時間（ミリ秒）を計算
      const startTime = new Date(startedAt).getTime();
      const endTime = new Date(endedAt).getTime();
      const durationInHours = (endTime - startTime) / (1000 * 60 * 60);

      // 基本料金（1時間1000円と仮定）
      const hourlyRate = 1000;
      const mockPrice = Math.floor(durationInHours * hourlyRate);

      const mockResponse = {
				price: mockPrice,
      };

      const duration = Date.now() - startTime;
			logApiResponse("GET", url, mockResponse, duration);
      return mockResponse.price;
    }

    const response = await APIHelper.get(url);
    const duration = Date.now() - startTime;
		logApiResponse("GET", url, response, duration);
    return response.price;
  } catch (error) {
		logApiError("GET", url, error);
    // エラー時は概算価格を返す（1時間1000円と仮定）
    const startTime = new Date(startedAt).getTime();
    const endTime = new Date(endedAt).getTime();
    const durationInHours = (endTime - startTime) / (1000 * 60 * 60);
    return Math.floor(durationInHours * 1000);
  }
};

/**
 * スペース情報と予約情報を統合して取得する
 * @param id - スペースID
 * @param date - 取得する週の開始日
 * @param weekNum - 取得する週数（デフォルト1）
 * @param encrypt - レスポンスを暗号化するかどうか（デフォルトはtrue）
 * @returns スペース情報と予約情報
 */
export const getSpaceBookings = async (
  id: number,
  date: Date,
  weekNum = 1,
  encrypt = true,
): Promise<SpaceData & ReservationsData> => {
  const formattedDate = formatAPIDate(date);
  const params = {
    date: formattedDate,
    week_num: weekNum,
    encrypt: encrypt ? undefined : 'false',
  };
  
  // encryptがfalseの場合のみクエリパラメータに追加
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value !== undefined)
  );
  
  const query = new URLSearchParams(paramsToString(filteredParams));
  const url = `/api/spaces/${id}/space_bookings.json?${query}`;

  logApiRequest("GET", url, { ...filteredParams, id });
  const startTime = Date.now();

  try {
    // モックデータ（デバッグ用）
    if (DEBUG && USE_MOCK) {
      // 0.5秒後にモックデータを返す（スペースデータと予約データのモックを組み合わせる）
      await new Promise((resolve) => setTimeout(resolve, 500));

      // スペース情報のモック
      const mockSpaceData = {
        id,
        title: `テストスペース #${id}`,
        data: [
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
          { start_at: "09:00", end_at: "22:00" },
        ],
        deadline: 60,
        reserve_days_ahead: 30,
      };

      // 予約情報のモック
      const mockReservations: Record<string, any[]> = {};
      // 日付文字列を生成
      const dateObj = new Date(date);
      const dates = [];
      for (let i = 0; i < 7 * weekNum; i++) {
        const d = new Date(dateObj);
        d.setDate(d.getDate() + i);
				dates.push(formatDate(d, "yyyy-MM-dd"));
      }

      for (const d of dates) {
        // 50%の確率で予約あり
        if (Math.random() > 0.5) {
          mockReservations[d] = [
            { started_at: "10:00", duration: 120 },
						{ started_at: "14:00", duration: 90 },
          ];
        } else {
          mockReservations[d] = [];
        }
      }

      // 組み合わせたデータ
      const mockData = {
        ...mockSpaceData,
				reservations: mockReservations,
      };

      const duration = Date.now() - startTime;
			logApiResponse("GET", url, mockData, duration);
      return mockData as SpaceData & ReservationsData;
    }

    const response = await APIHelper.get(url);
    const duration = Date.now() - startTime;

		logApiResponse("GET", url, response, duration);
    
    // deadline型変換とデバッグ
    if (DEBUG) {
      console.group('📊 スペースデータ詳細:');
      console.log('ID:', response.id);
      console.log('名前:', response.name);
      console.log('締切時間(変換前):', response.deadline);
      console.log('締切時間の型:', typeof response.deadline);
      console.groupEnd();
    }
    
    // レスポンスのコピーを作成して変更（イミュータブルに処理）
    const processedResponse = { ...response };
    
    // deadline値が文字列の場合は数値に変換
    if (typeof processedResponse.deadline === 'string') {
      processedResponse.deadline = Number.parseInt(processedResponse.deadline, 10);
      console.log('締切時間を数値に変換:', processedResponse.deadline);
    }
    
    // 数値に変換できなかった場合や0未満の値はデフォルト値に設定
    if (Number.isNaN(processedResponse.deadline) || processedResponse.deadline < 0) {
      console.warn('無効な締切時間値を検出:', processedResponse.deadline);
      processedResponse.deadline = 0;
    }
    
    // APIのレスポンス構造を調整（space.deadlineをトップレベルのdeadlineとしても設定）
    // これによりindex.tsxでdata.spaceとdataの両方の構造に対応
    if (processedResponse.space && typeof processedResponse.space === 'object') {
      if (processedResponse.space.deadline !== undefined) {
        // space.deadlineが存在する場合はそれをトップレベルにも設定
        processedResponse.deadline = processedResponse.space.deadline;
      } else if (processedResponse.deadline !== undefined) {
        // トップレベルのdeadlineがある場合はspaceにも設定
        processedResponse.space.deadline = processedResponse.deadline;
      }
    }
    
    console.log('処理後のレスポンス:', {
      トップレベルdeadline: processedResponse.deadline,
      space内deadline: processedResponse.space?.deadline
    });
    
    return processedResponse;
  } catch (error) {
		logApiError("GET", url, error);
    throw error;
  }
};

// 互換性のためにオブジェクトとしてエクスポート
const ApiService = {
  getSpaceData,
  getReservations,
  checkReservationAvailability,
  calculatePrice,
	getSpaceBookings,
};

export default ApiService;
